import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Store,
  Package,
  FileText,
  UserSquare2,
  ChevronLeft,
  ChevronRight,
  X,
  HeadphonesIcon,
  Receipt,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useHasRole } from '@/features/auth/AuthProvider';
import { useSidebarUI } from './sidebarUI';
import type { Role } from '@/types/schemas';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  allow?: Role[];
  tutorialId?: string;
};

const items: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/stores', label: 'Stores', icon: Store, allow: ['owner'], tutorialId: 'stores' },
  { to: '/datahub', label: 'Inventory', icon: Package, tutorialId: 'inventory' },
  { to: '/quotations', label: 'Quotation', icon: FileText, tutorialId: 'quotations' },
  { to: '/crm', label: 'CRM', icon: UserSquare2, tutorialId: 'crm' },
  { to: '/bills', label: 'Daily Reports', icon: Receipt },
  { to: '/support', label: 'Contact Support', icon: HeadphonesIcon },
];

type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
};

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { mobileOpen, setMobileOpen } = useSidebarUI();
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  const sidebarContent = (isMobile: boolean) => (
    <aside
      className={cn(
        'flex h-full flex-col border-r bg-ink text-cream',
        isMobile ? 'w-72' : cn(
          'transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-60',
        ),
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex border-b border-gold-500/20',
          !isMobile && collapsed
            ? 'flex-col items-center gap-2 py-3'
            : 'h-16 items-center justify-between gap-2 px-3 pl-4',
        )}
      >
        {!isMobile && collapsed ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-cream/80 hover:bg-cream/10 hover:text-cream"
              onClick={onToggle}
              aria-label="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <img src="/chhabighar.png" alt="" className="h-8 w-8 rounded-md object-contain" />
          </>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-3">
              <img src="/chhabighar.png" alt="" className="h-9 w-9 shrink-0 rounded-md object-contain" />
              <div className="min-w-0">
                <div className="font-serif text-base leading-tight tracking-wide text-cream">
                  CHHABIGHAR
                </div>
                <div className="text-[10px] uppercase tracking-widest text-cream/50">
                  Powered by NexaRise
                </div>
              </div>
            </div>
            {isMobile ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 text-cream/80 hover:bg-cream/10 hover:text-cream"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 text-cream/80 hover:bg-cream/10 hover:text-cream"
                onClick={onToggle}
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>

      {/* Nav */}
      <nav className={cn('flex-1 overflow-y-auto py-4', !isMobile && collapsed ? 'px-2' : 'px-3')}>
        <ul className="space-y-1">
          {items.map((item) => (
            <NavRow
              key={item.to}
              item={item}
              collapsed={!isMobile && collapsed}
            />
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div
        className={cn(
          'border-t border-gold-500/20 py-3 text-[11px] text-cream/40',
          !isMobile && collapsed ? 'px-2 text-center' : 'px-5',
        )}
      >
        {!isMobile && collapsed ? (
          <span className="block text-[10px] leading-tight text-cream/30" aria-hidden>
            v0.1
          </span>
        ) : (
          <>v0.1.0 · ছবি ঘর</>
        )}
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="desktop-sidebar hidden lg:flex lg:shrink-0">
        {sidebarContent(false)}
      </div>

      {/* Tablet sidebar — icon only, always visible */}
      <div className="hidden md:flex md:shrink-0 lg:hidden">
        <aside className="flex h-full w-16 flex-col border-r bg-ink text-cream">
          <div className="flex h-16 flex-col items-center justify-center border-b border-gold-500/20 py-3">
            <img src="/chhabighar.png" alt="" className="h-8 w-8 rounded-md object-contain" />
          </div>
          <nav className="flex-1 overflow-y-auto px-2 py-4">
            <ul className="space-y-1">
              {items.map((item) => (
                <NavRow key={item.to} item={item} collapsed={true} />
              ))}
            </ul>
          </nav>
          <div className="border-t border-gold-500/20 px-2 py-3 text-center">
            <span className="block text-[10px] leading-tight text-cream/30">v0.1</span>
          </div>
        </aside>
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          'mobile-drawer fixed inset-y-0 left-0 z-50 flex flex-col md:hidden',
          'transition-transform duration-300 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {sidebarContent(true)}
      </div>
    </>
  );
}

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const allowed = useHasRole(...(item.allow ?? ['owner', 'manager', 'employee']));
  if (!allowed) return null;
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        title={collapsed ? item.label : undefined}
        data-tutorial-id={item.tutorialId}
        className={({ isActive }) =>
          cn(
            'group flex items-center rounded-md py-2 text-sm font-medium transition-colors',
            collapsed ? 'justify-center px-2' : 'gap-3 px-3',
            isActive
              ? 'bg-gold-500/15 text-gold-300'
              : 'text-cream/70 hover:bg-cream/5 hover:text-cream',
          )
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && item.label}
      </NavLink>
    </li>
  );
}
